import type { BranchKey } from '@festina-life/api'
import { useTranslate } from '@festina-life/ui/hooks'
import { Tabs } from '@festina-life/ui/structure'
import { useMemo } from 'react'

import { useLayoutContext } from '../../../context/layoutContext'
import { useIsPersonaEditor } from '../../../uiModels/useIsPersonaEditor'
import { useWorkspaceOperations } from '../../../uiModels/workspace/useWorkspaceOperations'
import { isEqualWorkspace } from '../../../workspace/helpers'
import { useOperationsContext } from '../../../workspace/operationsContext'
import { countShadowedOperations } from '../../../workspace/shadowedOperationsHelpers'
import { useWorkspaceManagerContext } from '../../../workspace/workspaceManagerContext'
import { LocationErrorsPanel } from '../../errorsPanel/LocationErrorsPanel'
import { UnifiedShadowedOperationsPanel } from '../../shadowedPanel/UnifiedShadowedOperationsPanel'
import { AdvancedManageBranch } from './advancedManageBranch/AdvancedManageBranch'
import { BranchConciseChangesetList } from './branchHistory/BranchConciseChangesetList'
import { BranchWorkspacesPanel } from './branchWorkspaces/BranchWorkspacesPanel'
import { ManageBranch } from './manageBranch/ManageBranch'
import { type BranchDetailsTab, tab } from './types'
import { useActiveElementTabsState } from './useActiveElementTabsState'
import { WorkspaceChangesPanel } from './workspaceChangesPanel/WorkspaceChangesPanel'

type Props = {
  branchKey: BranchKey
}

export const UnifiedBranchWorkspaceDetailsTabs = ({ branchKey }: Props) => {
  const translate = useTranslate()
  const { persona } = useLayoutContext()
  const isPersonaEditor = useIsPersonaEditor()
  const {
    branchShadowedTemplateOperations,
    workspaceShadowedTemplateOperations,
    workspaceKey,
  } = useOperationsContext()
  const { currentWorkspaceKey } = useWorkspaceManagerContext()
  const { data: draftOperations } = useWorkspaceOperations(workspaceKey)
  const totalShadowedCount =
    countShadowedOperations(branchShadowedTemplateOperations) +
    countShadowedOperations(workspaceShadowedTemplateOperations)
  const shouldIncludeShadowedTab = totalShadowedCount > 0
  const isWorkspaceActive =
    currentWorkspaceKey && workspaceKey
      ? isEqualWorkspace(workspaceKey, currentWorkspaceKey)
      : false

  const tabItems: BranchDetailsTab[] = useMemo(
    () => [
      tab('manage', translate('branchDetailsPanel.tabs.manage.title')),
      ...(isWorkspaceActive
        ? [
            tab(
              'operations',
              translate('workspaceDetailsPanel.tabs.changes.title', {
                count: draftOperations?.length ?? 0,
              }),
            ),
          ]
        : []),
      tab('history', translate('branchDetailsPanel.tabs.history.title')),
      tab('review', translate('branchDetailsPanel.tabs.review.title')),
      tab('errors', translate('branchDetailsPanel.tabs.errors.title')),
      ...(shouldIncludeShadowedTab
        ? [
            tab(
              'shadowed',
              translate('branchDetailsPanel.tabs.shadowed.title', {
                count: totalShadowedCount,
              }),
            ),
          ]
        : []),
      ...(isPersonaEditor
        ? [
            tab(
              'workspaces',
              translate('branchDetailsPanel.tabs.workspaces.title'),
            ),
          ]
        : []),
      ...(persona === 'ConfigurationAdmin'
        ? [tab('advanced', translate('branchDetailsPanel.tabs.advanced.title'))]
        : []),
    ],
    [
      persona,
      isPersonaEditor,
      shouldIncludeShadowedTab,
      totalShadowedCount,
      draftOperations,
      translate,
      isWorkspaceActive,
    ],
  )

  const { selectedIndex, onTabChange } = useActiveElementTabsState(tabItems)
  const workspaceKeyForConciseFetching =
    workspaceKey && isWorkspaceActive ? workspaceKey : undefined

  return (
    <Tabs
      items={tabItems}
      selectedIndex={selectedIndex}
      onChange={onTabChange}
      panelClassName="overflow-auto h-full"
      tabGroupClassName="h-full flex flex-col"
      tabItemsClassName="w-full flex-wrap overflow-x-hidden!"
      shouldMountInactive
    >
      {[
        <ManageBranch key="manage" />,
        ...(isWorkspaceActive
          ? [<WorkspaceChangesPanel key="operations" />]
          : []),
        <BranchConciseChangesetList
          key="history"
          variant="history"
          branchKey={branchKey}
          workspaceKey={workspaceKeyForConciseFetching}
        />,
        <BranchConciseChangesetList
          key="review"
          variant="review"
          branchKey={branchKey}
          workspaceKey={workspaceKeyForConciseFetching}
        />,
        <LocationErrorsPanel key="errors" />,
        ...(shouldIncludeShadowedTab
          ? [<UnifiedShadowedOperationsPanel key="shadowed" />]
          : []),
        ...(isPersonaEditor
          ? [<BranchWorkspacesPanel key="workspaces" />]
          : []),
        ...(persona === 'ConfigurationAdmin'
          ? [<AdvancedManageBranch key="advanced" />]
          : []),
      ]}
    </Tabs>
  )
}
